import { useEffect, useRef, type ReactNode } from 'react'
import { Button } from './Button'
import { Close } from './Icons'
import { t } from '../i18n'

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: ReactNode
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const width = size === 'sm' ? 'max-w-sm' : size === 'xl' ? 'max-w-4xl' : size === 'lg' ? 'max-w-xl' : 'max-w-md'

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        // Click on the backdrop (outside the inner panel) closes the dialog.
        if (e.target === e.currentTarget) onClose()
      }}
      className={`m-auto w-[calc(100%-2rem)] ${width} rounded-[1.25rem] bg-transparent p-0 shadow-none open:animate-scale-in`}
    >
      <div className="shell">
        <div className="plate p-6 sm:p-7">
          <div className="mb-5 flex items-start justify-between gap-6">
            <div>
              <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-ink-950">{title}</h2>
              {description ? <p className="mt-1 text-[13.5px] leading-relaxed text-ink-500">{description}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="-mr-2 -mt-2 grid size-8 shrink-0 place-items-center rounded-[9px] text-ink-400 transition-[background-color,color,transform] duration-200 ease-spring hover:bg-ink-950/[0.05] hover:text-ink-900 active:scale-95"
            >
              <Close size={16} />
            </button>
          </div>
          {children}
        </div>
      </div>
    </dialog>
  )
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = t('common.confirm'),
  tone = 'danger',
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  tone?: 'danger' | 'primary'
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Modal open={open} onClose={onCancel} title={title} size="sm">
      <p className="text-[14px] leading-relaxed text-ink-600">{message}</p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant={tone} onClick={onConfirm} autoFocus>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  )
}
